
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

import { DateTime, Duration } from 'luxon'

import ModalDatetimePicker from '@/components/datetime/ModalDateTimePicker.vue'
import { TimeStepSequence } from '@/model/TimeStepSequence'
import LiveFreeSwitch from '@/components/commands/MainModeSwitch.vue'
import { ISnapshotLike } from '@/model/ISnapshotLike'
import { Representation, RepresentationTypes } from '@/model/Representation'
import { TimeStep } from '@/model/TimeStep'

@Component({
  components: {
    ModalDatetimePicker,
    LiveFreeSwitch,
  },
})
export default class DateTimeAxis extends Vue {
  @Prop() snapshot!: ISnapshotLike<TimeStep>
  @Prop({ type: Number, default: 3 }) ticksBefore!: number
  @Prop({ type: Number, default: 3 }) ticksAfter!: number
  @Prop() durationBefore!: Duration
  @Prop() durationAfter!: Duration
  @Prop({ type: Boolean, default: false }) activated!: boolean
  @Prop({ required: true }) representation!: Representation<RepresentationTypes>

  DEFAULT_INCREMENTS_MINUTES = 5
  DEFAULT_INCREMENTS_HOURS = 1

  timeSequence!: TimeStepSequence<TimeStep>
  ticks: number[] = []
  showModal = false

  created(): void {
    this.snapshotChanged()
    this.ticks = []
    for (let i = 0; i < this.ticksBefore; ++i) {
      this.ticks.push((i / this.ticksBefore) * 0.5)
    }
    this.ticks.push(0.5)
    for (let i = 1; i <= this.ticksAfter; ++i) {
      this.ticks.push(0.5 + (i / this.ticksAfter) * 0.5)
    }
  }

  /**
   * Format a given datetime timeline value.
   * */
  labelFor(tick: number): string {
    let dt = this.timeSequence.getDateTimeAt(tick).setZone(this.snapshot.city.getTimezone())

    // Round to alignment interval for cumulative rainfall
    if (this.representation.type === 'cumulative rainfall') {
      const interval = this.representation.cumulativeRainfall.cumulAgregationTimeMinutes
      const totalMinutes = dt.hour * 60 + dt.minute
      const roundedMinutes = interval * Math.floor(totalMinutes / interval)
      dt = dt.set({
        hour: Math.floor(roundedMinutes / 60) % 24,
        minute: roundedMinutes % 60,
        second: 0,
        millisecond: 0,
      })
    }

    const format = this.$t(tick === 0.5 ? 'common.datetime.format.datetime' : 'common.datetime.format.time').toString()
    return dt.toFormat(format)
  }

  /**
   * Returns the section matching this value.
   * */
  sectionFor(value: DateTime): string {
    return value.toString()
  }

  onPickDatetimeRequested(): void {
    this.showModal = true
  }

  onModalClosing(): void {
    this.showModal = false
  }

  get increments(): { incrementMinutes: number; incrementHours: number } {
    if (this.representation.type !== 'cumulative rainfall') {
      return { incrementMinutes: this.DEFAULT_INCREMENTS_MINUTES, incrementHours: this.DEFAULT_INCREMENTS_HOURS }
    }
    const crInterval = this.representation.cumulativeRainfall.cumulAgregationTimeMinutes
    if (crInterval < 60) {
      return { incrementMinutes: crInterval, incrementHours: this.DEFAULT_INCREMENTS_HOURS }
    } else {
      return { incrementMinutes: 60, incrementHours: crInterval / 60 }
    }
  }

  @Watch('snapshot')
  snapshotChanged(): void {
    this.timeSequence = TimeStepSequence.fromIterable(this.snapshot, this.snapshot.getStart(), this.snapshot.getEnd())
  }

  onDatetimeChanged(_newDt: DateTime): void {
    this.$emit('datetime-changed', this.snapshot.timestamp, _newDt)
  }
}
